import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { QuillLogo } from "../components/QuillLogo";
import { AdminNav } from "../components/AdminNav";
import { buildEmblemSvg, buildLockupSvg, buildLockupHorizontalSvg, buildBadgeSvg, buildOvalMonogramSvg, downloadText, downloadPng } from "../admin/logoAssets";
import { buildBrandSystemHtml } from "../admin/brandSystem";
import { buildBrandKitHtml } from "../admin/brandKit";
import { buildCardSpecHtml } from "../admin/cardSpec";
import { AuthorSignature } from "../components/AuthorSignature";
import { CopyExport } from "../components/CopyExport";
import { exportReferencePdf } from "../admin/pdfExport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CARD_DEFAULTS = {
  name: "Zukiswa Phoofolo",
  title: "Writer-Anthropologist",
  phone: "+27 79 281 8435",
  email: "info@theravensquill.co.za",
  website: "www.theravensquill.co.za",
};

const SECTIONS = [
  { id: "brand", label: "Brand System" },
  { id: "brandkit", label: "Brand Kit" },
  { id: "logo", label: "Logo" },
  { id: "card", label: "Business Card" },
  { id: "cardspec", label: "Card Spec" },
  { id: "copy", label: "Website Copy" },
];

// html2canvas positions the Herr Von Muellerhoff script baseline slightly lower
// than the browser, which pushes the signature curve up into the name on export.
// This onclone hook nudges the curve down in the cloned DOM so the exported
// PDF / PNG grazes beneath the name exactly like the on-screen preview.
const EXPORT_CURVE_OFFSET = 14; // px at scale 1
const signatureCurveOnClone = (docClone) => {
  docClone.querySelectorAll("[data-signature-curve]").forEach((el) => {
    const h = parseFloat(el.style.height) || 20;
    const scale = h / 20;
    const current = parseFloat(el.style.marginTop) || 0;
    el.style.marginTop = `${current + EXPORT_CURVE_OFFSET * scale}px`;
  });
};

const AdminStudio = () => {
  const navigate = useNavigate();
  const [section, setSection] = useState("brand");
  const token = typeof window !== "undefined" ? localStorage.getItem("rq_admin_token") : null;

  useEffect(() => {
    if (!token) navigate("/admin");
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-[#F4F1EA] pb-24" data-testid="admin-studio-page">
      <AdminNav active="studio" />
      <div className="mx-auto max-w-[900px] px-6 md:px-8 pt-14">
        <h1 className="text-[28px] md:text-[32px] italic text-[#1C1A17]">Studio Kit</h1>
        <p className="mt-4 text-[14px] text-[#6E6A5E]">
          Generate on-brand, downloadable artifacts.
        </p>

        <div className="mt-10 flex gap-6 border-b" style={{ borderColor: "rgba(168,162,150,0.4)" }}>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              data-testid={`studio-tab-${s.id}`}
              className={`pb-3 text-[11px] uppercase tracking-[0.28em] transition-colors ${
                section === s.id
                  ? "text-[#7A2E2E] border-b-2 border-[#7A2E2E]"
                  : "text-[#A8A296] hover:text-[#1C1A17]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="mt-12">
          {section === "brand" && <BrandStudio />}
          {section === "brandkit" && <BrandKitStudio />}
          {section === "logo" && <LogoStudio />}
          {section === "card" && <CardStudio token={token} />}
          {section === "cardspec" && <CardSpecStudio />}
          {section === "copy" && <CopyStudio />}
        </div>
      </div>
    </div>
  );
};

/* ----------------------------- Brand System ----------------------------- */
const BrandStudio = () => {
  const html = React.useMemo(() => buildBrandSystemHtml(), []);
  const iframeRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const btn =
    "text-[10px] uppercase tracking-[0.24em] border border-[#7A2E2E] text-[#7A2E2E] px-4 py-2 hover:bg-[#7A2E2E] hover:text-[#F4F1EA] transition-colors disabled:opacity-50";
  const dlPdf = async () => {
    if (busy) return;
    setBusy(true);
    try { await exportReferencePdf({ iframe: iframeRef.current, filename: "ravens-quill-brand-system.pdf" }); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="brand-studio">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <span className="block text-[11px] uppercase tracking-[0.3em] text-[#7A2E2E] mb-1">Reference</span>
          <h2 className="text-[22px] italic text-[#1C1A17]">Brand System &amp; Visual Standards</h2>
          <p className="mt-2 text-[13px] leading-relaxed text-[#6E6A5E] max-w-[60ch]">
            The complete standards, previewed live below. Fonts and every logo are embedded, so the
            downloaded PDF renders offline and prints cleanly.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button className={btn} data-testid="brand-system-download-pdf" onClick={dlPdf} disabled={busy}>
            {busy ? "Preparing PDF..." : "Download as PDF"}
          </button>
          <button
            className={btn}
            data-testid="brand-system-open"
            onClick={() => {
              const w = window.open("", "_blank");
              if (w) { w.document.write(html); w.document.close(); }
            }}
          >
            Open in New Tab
          </button>
          <button
            className={btn}
            data-testid="brand-system-download"
            onClick={() => downloadText("ravens-quill-brand-system.html", html, "text/html")}
          >
            Download HTML
          </button>
        </div>
      </div>
      <div className="border border-[#A8A296] bg-white" data-testid="brand-system-preview">
        <iframe
          ref={iframeRef}
          title="The Raven's Quill, Brand System"
          srcDoc={html}
          className="w-full block"
          style={{ height: "1400px", border: "0" }}
          onLoad={(e) => {
            try {
              const doc = e.target.contentDocument;
              if (doc && doc.body) e.target.style.height = doc.body.scrollHeight + 40 + "px";
            } catch (_) {}
          }}
        />
      </div>
    </div>
  );
};

/* ----------------------------- Brand Kit (Internal) ----------------------------- */
const BrandKitStudio = () => {
  const html = React.useMemo(() => buildBrandKitHtml(), []);
  const iframeRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const btn =
    "text-[10px] uppercase tracking-[0.24em] border border-[#7A2E2E] text-[#7A2E2E] px-4 py-2 hover:bg-[#7A2E2E] hover:text-[#F4F1EA] transition-colors disabled:opacity-50";
  const dlPdf = async () => {
    if (busy) return;
    setBusy(true);
    try { await exportReferencePdf({ iframe: iframeRef.current, filename: "ravens-quill-brand-kit-internal.pdf" }); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="brandkit-studio">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <span className="block text-[11px] uppercase tracking-[0.3em] text-[#7A2E2E] mb-1">Internal &middot; Confidential</span>
          <h2 className="text-[22px] italic text-[#1C1A17]">Brand &amp; Social Kit</h2>
          <p className="mt-2 text-[13px] leading-relaxed text-[#6E6A5E] max-w-[60ch]">
            The full strategy for the team, designers, and collaborators: story, values, positioning,
            voice, and messaging pillars, including the commercial logic. Not for client distribution.
            Fonts are embedded, so the downloaded PDF renders offline and prints cleanly.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button className={btn} data-testid="brand-kit-download-pdf" onClick={dlPdf} disabled={busy}>
            {busy ? "Preparing PDF..." : "Download as PDF"}
          </button>
          <button
            className={btn}
            data-testid="brand-kit-open"
            onClick={() => {
              const w = window.open("", "_blank");
              if (w) { w.document.write(html); w.document.close(); }
            }}
          >
            Open in New Tab
          </button>
          <button
            className={btn}
            data-testid="brand-kit-download"
            onClick={() => downloadText("ravens-quill-brand-kit-internal.html", html, "text/html")}
          >
            Download HTML
          </button>
        </div>
      </div>
      <div className="border border-[#A8A296] bg-white" data-testid="brand-kit-preview">
        <iframe
          ref={iframeRef}
          title="The Raven's Quill, Brand Kit (Internal Edition)"
          srcDoc={html}
          className="w-full block"
          style={{ height: "1400px", border: "0" }}
          onLoad={(e) => {
            try {
              const doc = e.target.contentDocument;
              if (doc && doc.body) e.target.style.height = doc.body.scrollHeight + 40 + "px";
            } catch (_) {}
          }}
        />
      </div>
    </div>
  );
};

/* ----------------------------- Website Copy ----------------------------- */
const CopyStudio = () => (
  <div className="space-y-6" data-testid="copy-studio">
    <div>
      <span className="block text-[11px] uppercase tracking-[0.3em] text-[#7A2E2E] mb-1">Internal</span>
      <h2 className="text-[22px] italic text-[#1C1A17]">Website Copy</h2>
      <p className="mt-2 text-[13px] leading-relaxed text-[#6E6A5E] max-w-[62ch]">
        Export the complete copy of every public page, Home, The Method, The Work, The Final
        Tribute, The Anthropologist, Commission, FAQ, and both writing samples, as a single PDF
        for proofreading, records, or sharing with an editor.
      </p>
    </div>
    <CopyExport />
  </div>
);

/* ----------------------------- Card Spec ----------------------------- */
const CardSpecStudio = () => {
  const html = React.useMemo(() => buildCardSpecHtml(), []);
  const iframeRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const btn =
    "text-[10px] uppercase tracking-[0.24em] border border-[#7A2E2E] text-[#7A2E2E] px-4 py-2 hover:bg-[#7A2E2E] hover:text-[#F4F1EA] transition-colors disabled:opacity-50";
  const dlPdf = async () => {
    if (busy) return;
    setBusy(true);
    try { await exportReferencePdf({ iframe: iframeRef.current, filename: "ravens-quill-business-card-spec.pdf" }); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="cardspec-studio">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <span className="block text-[11px] uppercase tracking-[0.3em] text-[#7A2E2E] mb-1">Reference</span>
          <h2 className="text-[22px] italic text-[#1C1A17]">Business Card, Printer Specification</h2>
          <p className="mt-2 text-[13px] leading-relaxed text-[#6E6A5E] max-w-[60ch]">
            The confirmed two-sided card: dimensions, colours, stock, and the raised foil / letterpress
            embellishments, ready to hand a printer. Previews and fonts are embedded, matching the live
            card generator, and the downloaded PDF prints cleanly.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button className={btn} data-testid="card-spec-download-pdf" onClick={dlPdf} disabled={busy}>
            {busy ? "Preparing PDF..." : "Download as PDF"}
          </button>
          <button
            className={btn}
            data-testid="card-spec-open"
            onClick={() => { const w = window.open("", "_blank"); if (w) { w.document.write(html); w.document.close(); } }}
          >
            Open in New Tab
          </button>
          <button
            className={btn}
            data-testid="card-spec-download"
            onClick={() => downloadText("ravens-quill-business-card-spec.html", html, "text/html")}
          >
            Download HTML
          </button>
        </div>
      </div>
      <div className="border border-[#A8A296] bg-white" data-testid="card-spec-preview">
        <iframe
          ref={iframeRef}
          title="Business Card, Printer Specification"
          srcDoc={html}
          className="w-full block"
          style={{ height: "1400px", border: "0" }}
          onLoad={(e) => {
            try {
              const doc = e.target.contentDocument;
              if (doc && doc.body) e.target.style.height = doc.body.scrollHeight + 40 + "px";
            } catch (_) {}
          }}
        />
      </div>
    </div>
  );
};

/* ----------------------------- Logo ----------------------------- */
const LogoStudio = () => {  const btn =
    "text-[10px] uppercase tracking-[0.24em] border border-[#7A2E2E] text-[#7A2E2E] px-4 py-2 hover:bg-[#7A2E2E] hover:text-[#F4F1EA] transition-colors";
  const btnWhite =
    "text-[10px] uppercase tracking-[0.24em] border border-[#1C1A17] bg-[#1C1A17] text-[#F4F1EA] px-4 py-2 hover:bg-[#000000] transition-colors";
  return (
    <div className="space-y-14" data-testid="logo-studio">
      {/* Emblem, ink */}
      <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-8 items-center">
        <div className="flex items-center justify-center bg-[#F4F1EA] border border-[#A8A296] p-8">
          <QuillLogo height={120} variant="ink" />
        </div>
        <div>
          <h2 className="text-[20px] italic text-[#1C1A17]">Emblem, Ink</h2>
          <p className="mt-2 text-[13px] text-[#6E6A5E]">For light backgrounds.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button className={btn} data-testid="logo-ink-svg" onClick={() => downloadText("ravens-quill-emblem-ink.svg", buildEmblemSvg("ink"))}>SVG</button>
            <button className={btn} data-testid="logo-ink-png-512" onClick={() => downloadPng(buildEmblemSvg("ink"), "ravens-quill-emblem-ink-512.png", 512, 1536)}>PNG 512</button>
            <button className={btn} data-testid="logo-ink-png-1024" onClick={() => downloadPng(buildEmblemSvg("ink"), "ravens-quill-emblem-ink-1024.png", 1024, 3072)}>PNG 1024</button>
            <button className={btnWhite} data-testid="logo-ink-white-svg" onClick={() => downloadText("ravens-quill-emblem-white.svg", buildEmblemSvg("white"))}>White SVG</button>
            <button className={btnWhite} data-testid="logo-ink-white-png" onClick={() => downloadPng(buildEmblemSvg("white"), "ravens-quill-emblem-white-1024.png", 1024, 3072)}>White PNG</button>
          </div>
        </div>
      </div>

      {/* Emblem, silver on dark */}
      <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-8 items-center">
        <div className="flex items-center justify-center bg-[#1C1A17] border border-[#1C1A17] p-8">
          <QuillLogo height={120} variant="gold" />
        </div>
        <div>
          <h2 className="text-[20px] italic text-[#1C1A17]">Emblem, Silver</h2>
          <p className="mt-2 text-[13px] text-[#6E6A5E]">Monochrome silver for dark backgrounds. Transparent PNG.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button className={btn} data-testid="logo-gold-svg" onClick={() => downloadText("ravens-quill-emblem-silver.svg", buildEmblemSvg("gold"))}>SVG</button>
            <button className={btn} data-testid="logo-gold-png-512" onClick={() => downloadPng(buildEmblemSvg("gold"), "ravens-quill-emblem-silver-512.png", 512, 1536)}>PNG 512</button>
            <button className={btn} data-testid="logo-gold-png-1024" onClick={() => downloadPng(buildEmblemSvg("gold"), "ravens-quill-emblem-silver-1024.png", 1024, 3072)}>PNG 1024</button>
            <button className={btnWhite} data-testid="logo-gold-white-svg" onClick={() => downloadText("ravens-quill-emblem-white.svg", buildEmblemSvg("white"))}>White SVG</button>
            <button className={btnWhite} data-testid="logo-gold-white-png" onClick={() => downloadPng(buildEmblemSvg("white"), "ravens-quill-emblem-white-1024.png", 1024, 3072)}>White PNG</button>
          </div>
        </div>
      </div>

      {/* Full lockup — Option 1: horizontal (matches the site header) */}
      <div className="grid grid-cols-1 sm:grid-cols-[300px_1fr] gap-8 items-center">
        <div className="flex items-center justify-center bg-[#F4F1EA] border border-[#A8A296] p-10">
          <div
            style={{ width: 220 }}
            data-testid="logo-lockup-preview"
            dangerouslySetInnerHTML={{ __html: buildLockupHorizontalSvg("ink") }}
          />
        </div>
        <div>
          <h2 className="text-[20px] italic text-[#1C1A17]">Full Lockup</h2>
          <p className="mt-2 text-[13px] text-[#6E6A5E]">Emblem beside the wordmark and tagline. Matches the site header.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button className={btn} data-testid="logo-lockup-svg" onClick={() => downloadText("ravens-quill-lockup.svg", buildLockupHorizontalSvg("ink"))}>SVG</button>
            <button className={btn} data-testid="logo-lockup-png" onClick={() => downloadPng(buildLockupHorizontalSvg("ink"), "ravens-quill-lockup-1200.png", 1200, 511)}>PNG 1200</button>
            <button className={btnWhite} data-testid="logo-lockup-white-svg" onClick={() => downloadText("ravens-quill-lockup-white.svg", buildLockupHorizontalSvg("white"))}>White SVG</button>
            <button className={btnWhite} data-testid="logo-lockup-white-png" onClick={() => downloadPng(buildLockupHorizontalSvg("white"), "ravens-quill-lockup-white-1200.png", 1200, 511)}>White PNG</button>
          </div>
        </div>
      </div>

      {/* Full lockup — Option 2: stacked / emblem on top */}
      <div className="grid grid-cols-1 sm:grid-cols-[300px_1fr] gap-8 items-center">
        <div className="flex items-center justify-center bg-[#F4F1EA] border border-[#A8A296] p-10">
          <div
            style={{ width: 220 }}
            data-testid="logo-lockup-stacked-preview"
            dangerouslySetInnerHTML={{ __html: buildLockupSvg("ink") }}
          />
        </div>
        <div>
          <h2 className="text-[20px] italic text-[#1C1A17]">Full Lockup, Stacked</h2>
          <p className="mt-2 text-[13px] text-[#6E6A5E]">Alternative arrangement with the emblem centred above the wordmark.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button className={btn} data-testid="logo-lockup-stacked-svg" onClick={() => downloadText("ravens-quill-lockup-stacked.svg", buildLockupSvg("ink"))}>SVG</button>
            <button className={btn} data-testid="logo-lockup-stacked-png" onClick={() => downloadPng(buildLockupSvg("ink"), "ravens-quill-lockup-stacked-1200.png", 1200, 707)}>PNG 1200</button>
            <button className={btnWhite} data-testid="logo-lockup-stacked-white-svg" onClick={() => downloadText("ravens-quill-lockup-stacked-white.svg", buildLockupSvg("white"))}>White SVG</button>
            <button className={btnWhite} data-testid="logo-lockup-stacked-white-png" onClick={() => downloadPng(buildLockupSvg("white"), "ravens-quill-lockup-stacked-white-1200.png", 1200, 707)}>White PNG</button>
          </div>
        </div>
      </div>

      {/* Monogram seal — circular badge for social media */}
      <div className="grid grid-cols-1 sm:grid-cols-[300px_1fr] gap-8 items-center">
        <div className="flex items-center justify-center bg-[#1C1A17] border border-[#A8A296] p-8">
          <div
            style={{ width: 200 }}
            data-testid="logo-badge-preview"
            dangerouslySetInnerHTML={{ __html: buildBadgeSvg("white") }}
          />
        </div>
        <div>
          <h2 className="text-[20px] italic text-[#1C1A17]">Monogram Seal</h2>
          <p className="mt-2 text-[13px] text-[#6E6A5E]">Circular "Z P" initials with the tagline arced around, for social avatars and watermarks. Transparent background.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button className={btn} data-testid="logo-badge-svg" onClick={() => downloadText("ravens-quill-monogram.svg", buildBadgeSvg("ink"))}>Ink SVG</button>
            <button className={btn} data-testid="logo-badge-png" onClick={() => downloadPng(buildBadgeSvg("ink"), "ravens-quill-monogram-1200.png", 1200, 1200)}>Ink PNG</button>
            <button className={btn} data-testid="logo-badge-black-svg" onClick={() => downloadText("ravens-quill-monogram-black.svg", buildBadgeSvg("black"))}>Black SVG</button>
            <button className={btn} data-testid="logo-badge-black-png" onClick={() => downloadPng(buildBadgeSvg("black"), "ravens-quill-monogram-black-1200.png", 1200, 1200)}>Black PNG</button>
            <button className={btnWhite} data-testid="logo-badge-white-svg" onClick={() => downloadText("ravens-quill-monogram-white.svg", buildBadgeSvg("white"))}>White SVG</button>
            <button className={btnWhite} data-testid="logo-badge-white-png" onClick={() => downloadPng(buildBadgeSvg("white"), "ravens-quill-monogram-white-1200.png", 1200, 1200)}>White PNG</button>
          </div>
        </div>
      </div>

      {/* Oval monogram — thin portrait ellipse framing the Z / P initials */}
      <div className="grid grid-cols-1 sm:grid-cols-[300px_1fr] gap-8 items-center">
        <div className="flex items-center justify-center bg-[#1C1A17] border border-[#A8A296] p-8">
          <div
            style={{ width: 190 }}
            data-testid="logo-oval-preview"
            dangerouslySetInnerHTML={{ __html: buildOvalMonogramSvg("white") }}
          />
        </div>
        <div>
          <h2 className="text-[20px] italic text-[#1C1A17]">Oval Monogram</h2>
          <p className="mt-2 text-[13px] text-[#6E6A5E]">The "Z P" initials framed by a slim portrait oval, for social avatars and watermarks. Transparent background.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button className={btn} data-testid="logo-oval-svg" onClick={() => downloadText("ravens-quill-oval.svg", buildOvalMonogramSvg("ink"))}>Ink SVG</button>
            <button className={btn} data-testid="logo-oval-png" onClick={() => downloadPng(buildOvalMonogramSvg("ink"), "ravens-quill-oval-1200.png", 1200, 1200)}>Ink PNG</button>
            <button className={btn} data-testid="logo-oval-black-svg" onClick={() => downloadText("ravens-quill-oval-black.svg", buildOvalMonogramSvg("black"))}>Black SVG</button>
            <button className={btn} data-testid="logo-oval-black-png" onClick={() => downloadPng(buildOvalMonogramSvg("black"), "ravens-quill-oval-black-1200.png", 1200, 1200)}>Black PNG</button>
            <button className={btnWhite} data-testid="logo-oval-white-svg" onClick={() => downloadText("ravens-quill-oval-white.svg", buildOvalMonogramSvg("white"))}>White SVG</button>
            <button className={btnWhite} data-testid="logo-oval-white-png" onClick={() => downloadPng(buildOvalMonogramSvg("white"), "ravens-quill-oval-white-1200.png", 1200, 1200)}>White PNG</button>
          </div>
        </div>
      </div>

      <SignatureExport />
    </div>
  );
};

// Name + title signature mark, downloadable for social posts (same design as the card).
const SignatureExport = () => {
  const ref = useRef(null);
  const whiteRef = useRef(null);
  const btn =
    "text-[10px] uppercase tracking-[0.24em] border border-[#7A2E2E] text-[#7A2E2E] px-4 py-2 hover:bg-[#7A2E2E] hover:text-[#F4F1EA] transition-colors";
  const btnWhite =
    "text-[10px] uppercase tracking-[0.24em] border border-[#1C1A17] bg-[#1C1A17] text-[#F4F1EA] px-4 py-2 hover:bg-[#000000] transition-colors";
  const download = async (targetRef, transparent, filename) => {
    if (document.fonts && document.fonts.ready) await document.fonts.ready;
    const canvas = await html2canvas(targetRef.current, {
      scale: 4,
      backgroundColor: transparent ? null : "#F4F1EA",
      useCORS: true,
      logging: false,
      onclone: signatureCurveOnClone,
    });
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = filename;
    a.click();
  };
  return (
    <div data-testid="signature-export">
      <h2 className="text-[20px] italic text-[#1C1A17]">Name Signature</h2>
      <p className="mt-2 text-[13px] text-[#6E6A5E]">The same signature mark as the card, for social posts and email footers.</p>
      <div className="mt-5 inline-block bg-[#F4F1EA] border border-[#A8A296] px-12 py-10">
        <div ref={ref} className="px-3 py-3">
          <AuthorSignature name={CARD_DEFAULTS.name} title={CARD_DEFAULTS.title} scale={0.9} />
        </div>
      </div>
      {/* Hidden white signature, captured for the dark-background export */}
      <div style={{ position: "absolute", left: -10000, top: 0 }} aria-hidden="true">
        <div ref={whiteRef} className="px-3 py-3">
          <AuthorSignature
            name={CARD_DEFAULTS.name}
            title={CARD_DEFAULTS.title}
            scale={0.9}
            nameColor="#FFFFFF"
            accent="#FFFFFF"
            titleColor="#FFFFFF"
          />
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-3">
        <button className={btn} data-testid="signature-png-transparent" onClick={() => download(ref, true, "ravens-quill-signature.png")}>PNG (transparent)</button>
        <button className={btn} data-testid="signature-png-paper" onClick={() => download(ref, false, "ravens-quill-signature-paper.png")}>PNG (on paper)</button>
        <button className={btnWhite} data-testid="signature-png-white" onClick={() => download(whiteRef, true, "ravens-quill-signature-white.png")}>White PNG</button>
      </div>
    </div>
  );
};

/* ----------------------------- Business Card ----------------------------- */
const CardStudio = ({ token }) => {
  const [fields, setFields] = useState(CARD_DEFAULTS);
  const [saved, setSaved] = useState([]);
  const [busy, setBusy] = useState(false);
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const frontSepRef = useRef(null);
  const backFoilRef = useRef(null);
  const backOxRef = useRef(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/artifacts?type=business_card`, auth);
      setSaved(data);
    } catch {
      /* no-op */
    }
  };
  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));

  const capture = async (el) => {
    if (document.fonts && document.fonts.ready) await document.fonts.ready;
    const canvas = await html2canvas(el, { scale: 3, backgroundColor: null, useCORS: true, logging: false, onclone: signatureCurveOnClone });
    return canvas.toDataURL("image/png");
  };

  // White-ground capture for the black-on-white printer separation layers.
  const captureWhite = async (el) => {
    if (document.fonts && document.fonts.ready) await document.fonts.ready;
    const canvas = await html2canvas(el, { scale: 3, backgroundColor: "#ffffff", useCORS: true, logging: false, onclone: signatureCurveOnClone });
    return canvas.toDataURL("image/png");
  };

  const downloadPdf = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const front = await capture(frontRef.current);
      const back = await capture(backRef.current);
      const frontSep = await captureWhite(frontSepRef.current);
      const foilSep = await captureWhite(backFoilRef.current);
      const oxSep = await captureWhite(backOxRef.current);
      // 90 x 55 mm trim + 3 mm bleed = 96 x 61 mm
      const W = 96, H = 61, bleed = 3;
      const pdf = new jsPDF({ unit: "mm", format: [W, H], orientation: "landscape" });
      const cropMarks = () => {
        pdf.setDrawColor(120);
        pdf.setLineWidth(0.1);
        const t = bleed, m = 3;
        [[t, t, 1, 1], [W - t, t, -1, 1], [t, H - t, 1, -1], [W - t, H - t, -1, -1]].forEach(([x, y, dx, dy]) => {
          pdf.line(x, y, x + dx * m, y);
          pdf.line(x, y, x, y + dy * m);
        });
      };
      const addSide = (img, first, label) => {
        if (!first) pdf.addPage([W, H], "landscape");
        pdf.addImage(img, "PNG", 0, 0, W, H);
        cropMarks();
        if (label) {
          pdf.setFontSize(4.5);
          pdf.setTextColor(120);
          pdf.text(label, bleed + 0.5, 1.9);
        }
      };
      // Colour artwork: page 1 front, page 2 back
      addSide(front, true);
      addSide(back, false);
      // Registered spot-layer separations (black = treatment area)
      addSide(frontSep, false, "SEPARATION - FRONT - LETTERPRESS / DEBOSS: name (die area in black)");
      addSide(foilSep, false, "SEPARATION - BACK - AGED PEWTER FOIL + DEBOSS: feather quill (nib to oxblood per proof)");
      addSide(oxSep, false, "SEPARATION - BACK - OXBLOOD: rules + tagline (foil or letterpress)");
      pdf.save(`ravens-quill-business-card-${fields.name.replace(/\s+/g, "-").toLowerCase()}.pdf`);
    } finally {
      setBusy(false);
    }
  };

  const downloadPngSide = async (ref, side) => {
    const img = await capture(ref.current);
    const a = document.createElement("a");
    a.href = img;
    a.download = `ravens-quill-card-${side}.png`;
    a.click();
  };

  const saveToHistory = async () => {
    try {
      await axios.post(
        `${API}/artifacts`,
        { type: "business_card", title: `Business Card · ${fields.name}`, data: fields },
        auth
      );
      loadHistory();
    } catch {
      /* no-op */
    }
  };

  const removeSaved = async (id) => {
    try {
      await axios.delete(`${API}/artifacts/${id}`, auth);
      setSaved((list) => list.filter((s) => s.id !== id));
    } catch {
      /* no-op */
    }
  };

  const input =
    "w-full border-0 border-b border-[#A8A296] bg-transparent py-2 text-[15px] outline-none focus:border-[#7A2E2E] text-[#1C1A17] rounded-none";
  const btn =
    "text-[10px] uppercase tracking-[0.24em] border border-[#7A2E2E] text-[#7A2E2E] px-4 py-2 hover:bg-[#7A2E2E] hover:text-[#F4F1EA] transition-colors disabled:opacity-60";

  return (
    <div data-testid="card-studio">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        {/* Form */}
        <div className="space-y-6">
          {[
            ["name", "Name"],
            ["title", "Title"],
            ["phone", "Telephone / WhatsApp"],
            ["email", "Email"],
            ["website", "Website"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="block text-[10px] uppercase tracking-[0.28em] text-[#7A2E2E] mb-2">{label}</label>
              <input className={input} value={fields[k]} onChange={set(k)} data-testid={`card-input-${k}`} />
            </div>
          ))}
          <div className="flex flex-wrap gap-3 pt-2">
            <button className={btn} onClick={downloadPdf} disabled={busy} data-testid="card-download-pdf">
              {busy ? "Preparing" : "Print-ready PDF"}
            </button>
            <button className={btn} onClick={() => downloadPngSide(frontRef, "front")} data-testid="card-download-front-png">Front PNG</button>
            <button className={btn} onClick={() => downloadPngSide(backRef, "back")} data-testid="card-download-back-png">Back PNG</button>
            <button className={btn} onClick={saveToHistory} data-testid="card-save">Save</button>
          </div>
        </div>

        {/* Previews */}
        <div className="space-y-6">
          <div className="inline-block shadow-[0_2px_14px_rgba(28,26,23,0.12)]">
            <CardFront ref={frontRef} fields={fields} />
          </div>
          <div className="inline-block shadow-[0_2px_14px_rgba(28,26,23,0.12)]">
            <CardBack ref={backRef} fields={fields} />
          </div>
          <p className="text-[12px] text-[#6E6A5E]">Preview shown with full bleed (90 x 55 mm trim, 3 mm bleed). The print-ready PDF adds crop marks and three registered spot-layer separations (letterpress name, aged-pewter foil quill, oxblood rules and tagline) for the printer's dies.</p>
        </div>
      </div>

      {/* Hidden, off-screen separation layers captured for the print-ready PDF.
          Black = treatment area, on white, registered to the colour artwork. */}
      <div style={{ position: "absolute", left: -10000, top: 0 }} aria-hidden="true">
        <div ref={frontSepRef} style={{ width: 480, height: 305 }} className="relative bg-white overflow-hidden flex items-center justify-center">
          <AuthorSignature name={fields.name} title={fields.title} nameColor="#000000" accent="#000000" titleColor="#000000" />
        </div>
        <div ref={backFoilRef} style={{ width: 480, height: 305 }} className="relative bg-white overflow-hidden flex flex-col items-center justify-center px-10">
          <div style={{ width: 260 }} dangerouslySetInnerHTML={{ __html: buildLockupSvg("ink", { word: "#FFFFFF", accent: "#FFFFFF", tagColor: "#FFFFFF" }) }} />
          <div className="mt-4 text-center text-[10.5px] leading-[1.85]" style={{ color: "#FFFFFF" }}>
            <p>{fields.phone}</p><p>{fields.email}</p><p>{fields.website}</p>
          </div>
        </div>
        <div ref={backOxRef} style={{ width: 480, height: 305 }} className="relative bg-white overflow-hidden flex flex-col items-center justify-center px-10">
          <div style={{ width: 260 }} dangerouslySetInnerHTML={{ __html: buildLockupSvg("white", { accent: "#000000", tagColor: "#000000" }) }} />
          <div className="mt-4 text-center text-[10.5px] leading-[1.85]" style={{ color: "#FFFFFF" }}>
            <p>{fields.phone}</p><p>{fields.email}</p><p>{fields.website}</p>
          </div>
        </div>
      </div>

      {/* History */}
      <div className="mt-16">
        <h2 className="text-[16px] italic text-[#1C1A17]">Saved cards</h2>
        {saved.length === 0 ? (
          <p className="mt-3 text-[13px] italic text-[#6E6A5E]" data-testid="card-history-empty">Nothing saved yet.</p>
        ) : (
          <div className="mt-4 space-y-3" data-testid="card-history">
            {saved.map((s) => (
              <div key={s.id} className="flex items-center justify-between border-b pb-3" style={{ borderColor: "rgba(168,162,150,0.4)" }}>
                <span className="text-[14px] text-[#1C1A17]">{s.title}</span>
                <div className="flex gap-5">
                  <button className="text-[10px] uppercase tracking-[0.24em] text-[#6E6A5E] hover:text-[#1C1A17]" onClick={() => setFields({ ...CARD_DEFAULTS, ...s.data })} data-testid="card-history-load">Load</button>
                  <button className="text-[10px] uppercase tracking-[0.24em] text-[#6E6A5E] hover:text-[#7A2E2E]" onClick={() => removeSaved(s.id)} data-testid="card-history-delete">Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Full-bleed card previews (aspect 96:61). Rendered at a fixed pixel width for capture.
// Front: Antique Paper, emblem top-left, name + title, contacts bottom-right.
// Front: Antique Paper, centred signature mark (name + curved flourish + title).
const CardFront = React.forwardRef(({ fields }, ref) => (
  <div
    ref={ref}
    data-testid="card-front"
    style={{ width: 480, height: 305 }}
    className="relative bg-[#F4F1EA] overflow-hidden flex items-center justify-center"
  >
    <AuthorSignature name={fields.name} title={fields.title} />
  </div>
));

// Back: Ink Black, centred emblem + wordmark + tagline, with contact details below.
const CardBack = React.forwardRef(({ fields }, ref) => (
  <div
    ref={ref}
    data-testid="card-back"
    style={{ width: 480, height: 305 }}
    className="relative bg-[#1C1A17] overflow-hidden flex flex-col items-center justify-center px-10"
  >
    <div
        style={{ width: 260 }}
        dangerouslySetInnerHTML={{
          __html: buildLockupSvg("gold", { word: "#F4F1EA", accent: "#B0573F", tagColor: "#B0573F", emblemTransform: "translate(136,6) scale(0.215)" }),
        }}
      />
      <div className="mt-4 text-center text-[10.5px] leading-[1.85]" style={{ color: "#DAD3C5" }}>
      <p>{fields.phone}</p>
      <p>{fields.email}</p>
      <p>{fields.website}</p>
    </div>
  </div>
));

export default AdminStudio;
